import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  resolverJanela24h,
  formatarTempoRestante,
  type Janela24hEstado,
} from '@/lib/janela-24h';

interface MensagemLike {
  criado_em: string;
  remetente: string;
}

interface Janela24hBadgeProps {
  mensagens: MensagemLike[] | undefined;
}

// Só a cor do texto por estado — sem fundo nem indicador.
const COR_TEXTO: Record<Exclude<Janela24hEstado, 'nenhuma'>, string> = {
  aberta: '#1a8f48',
  a_fechar: '#b45309',
  fechada: 'hsl(var(--destructive))',
};

export function Janela24hBadge({ mensagens }: Janela24hBadgeProps) {
  const { t } = useTranslation();
  const [agora, setAgora] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setAgora(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const ultimaMsgClienteEm = useMemo(() => {
    if (!mensagens) return null;
    let max: number | null = null;
    for (const m of mensagens) {
      if (m.remetente !== 'cliente') continue;
      const ts = new Date(m.criado_em).getTime();
      if (max === null || ts > max) max = ts;
    }
    return max === null ? null : new Date(max);
  }, [mensagens]);

  const janela = resolverJanela24h(ultimaMsgClienteEm, agora);
  if (janela.estado === 'nenhuma') return null;

  let label: string;
  if (janela.estado === 'fechada') {
    label = t('panel.window24h.closed');
  } else {
    const tempo = formatarTempoRestante(janela.msRestantes ?? 0);
    label =
      janela.estado === 'a_fechar'
        ? t('panel.window24h.closingSoon', { time: tempo })
        : t('panel.window24h.openFree', { time: tempo });
  }

  return (
    <span
      style={{
        color: COR_TEXTO[janela.estado],
        fontSize: 11.5,
        fontWeight: 600,
        marginLeft: 8,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}
